import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { WHATSAPP_URL, WhatsAppIcon } from "../pages/Contact";
import CountrySelector from "./CountrySelector";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const boutiqueLinks = [
  { label: "Le Bien-être", href: "/#chapitre-01" },
  { label: "L'Éclat", href: "/#chapitre-02" },
  { label: "Le Mouvement", href: "/#chapitre-03" },
  { label: "Le Style & le Voyage", href: "/voyage-decoration#chapitre-04" },
  { label: "La Décoration", href: "/voyage-decoration#chapitre-05" },
];

const infoLinks = [
  { label: "Mentions légales", to: "/mentions-legales", testid: "footer-mentions-legales" },
  { label: "Conditions générales de vente", to: "/cgv", testid: "footer-cgv" },
  { label: "Politique de confidentialité", to: "/confidentialite", testid: "footer-confidentialite" },
  { label: "Politique d'expédition", to: "/livraison-retours", testid: "footer-livraison" },
];

function Newsletter() {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!consent) {
      setError("Merci de cocher la case de consentement pour vous inscrire.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email, consent });
      setDone(true);
    } catch {
      setError("Une erreur est survenue, veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <p data-testid="newsletter-success" className="text-sm text-gold font-light leading-relaxed">
        ✦ Merci, votre inscription est confirmée. À très vite dans votre boîte aux lettres.
      </p>
    );
  }
  return (
    <form data-testid="newsletter-form" onSubmit={submit} className="space-y-4">
      <div className="flex items-center border border-ivory/40 focus-within:border-gold px-4 py-1 transition-colors bg-ivory/5">
        <input
          data-testid="newsletter-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Votre adresse e-mail"
          className="flex-1 min-w-0 bg-transparent outline-none py-2.5 text-sm placeholder:text-ivory/40 font-light text-gold"
        />
        <button
          data-testid="newsletter-submit"
          type="submit"
          disabled={loading}
          className="shrink-0 text-xs uppercase tracking-[0.2em] text-gold hover:text-ivory transition-colors disabled:opacity-50 pl-4 whitespace-nowrap"
        >
          {loading ? "…" : "S'inscrire"}
        </button>
      </div>
      <label className="flex items-start gap-3 cursor-pointer group">
        <input
          data-testid="newsletter-consent"
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5 accent-[#C9A15C]"
        />
        <span className="text-xs font-light text-ivory/85 leading-relaxed group-hover:text-ivory/80 transition-colors">
          J'accepte de recevoir la lettre KARYNA et les nouveautés de la boutique par e-mail.
          Je peux me désinscrire à tout moment.
        </span>
      </label>
      {error && <p data-testid="newsletter-error" className="text-xs text-gold">{error}</p>}
    </form>
  );
}

export default function Footer() {
  return (
    <footer id="contact" data-testid="site-footer" className="bg-choco-deep text-ivory/80 pt-10 pb-6">
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-col md:flex-row md:justify-between gap-10">
        <div data-testid="footer-boutique">
          <h3 className="text-xs uppercase tracking-[0.3em] text-gold">Boutique</h3>
          <ul className="mt-5 space-y-3 text-sm font-light">
            {boutiqueLinks.map((l) => (
              <li key={l.href}>
                <a href={l.href} className="hover:text-gold transition-colors duration-300">{l.label}</a>
              </li>
            ))}
          </ul>
        </div>
        <div data-testid="footer-newsletter" className="md:w-[340px] lg:w-[380px] md:shrink-0">
          <h3 className="text-xs uppercase tracking-[0.3em] text-gold">La lettre KARYNA</h3>
          <p className="mt-5 mb-5 text-sm font-light text-ivory/85 leading-relaxed">
            Nouveautés, rituels bien-être et offres douces, une fois par mois.
          </p>
          <Newsletter />
        </div>
        <div data-testid="footer-informations">
          <h3 className="text-xs uppercase tracking-[0.3em] text-gold">Informations</h3>
          <ul className="mt-5 space-y-3 text-sm font-light">
            {infoLinks.map((l) => (
              <li key={l.to}>
                <Link data-testid={l.testid} to={l.to} className="hover:text-gold transition-colors duration-300">{l.label}</Link>
              </li>
            ))}
            <li>
              <Link data-testid="footer-contact-link" to="/contact" className="hover:text-gold transition-colors duration-300">Contact — via notre page dédiée</Link>
            </li>
            <li>
              <a
                data-testid="footer-whatsapp"
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 hover:text-gold transition-colors duration-300"
              >
                <span className="w-5 h-5 rounded-full bg-[#25D366] text-white flex items-center justify-center">
                  <WhatsAppIcon size={12} />
                </span>
                WhatsApp Business — réponse rapide
              </a>
            </li>
          </ul>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 md:px-10 mt-10 pt-6 border-t border-ivory/10">
        <div className="flex flex-col items-center gap-8 md:flex-row md:items-start md:justify-between">
          <div data-testid="footer-country" className="text-center md:text-left md:pt-1">
            <p className="text-xs uppercase tracking-[0.25em] text-ivory/75 mb-3">Pays/Région</p>
            <CountrySelector variant="footer" />
          </div>
          <div className="text-center">
            <p className="text-xs uppercase tracking-[0.25em] text-ivory/75 mb-4">Moyens de paiement sécurisés — activation prochaine</p>
            <div data-testid="payment-methods" className="flex flex-wrap items-center justify-center gap-1.5">
              <span className="pay-badge !bg-[#2E77BC] !text-white text-[9px] font-bold tracking-wide">AMEX</span>
              <span className="pay-badge !bg-black !text-white text-[10px] font-semibold tracking-wide">Apple&nbsp;Pay</span>
              <span className="pay-badge !bg-white !text-[#005696] text-[9px] font-bold">Bancontact</span>
              <span className="pay-badge !bg-white text-[10px] font-semibold tracking-widest"><span className="text-[#1B4F9C]">C</span><span className="text-[#005696]">B</span></span>
              <span className="pay-badge !bg-white" aria-label="Mastercard">
                <span className="w-3 h-3 rounded-full bg-[#EB001B] inline-block" />
                <span className="w-3 h-3 rounded-full bg-[#F79E1B] inline-block -ml-1.5" />
              </span>
              <span className="pay-badge !bg-[#1A1F71] !text-white font-bold italic text-[10px] tracking-wider">VISA</span>
              <span className="pay-badge !bg-[#FFB3C7] !text-black text-[10px] font-semibold">Klarna<span className="font-black">.</span></span>
            </div>
          </div>
          <p className="text-ivory/40 text-xs font-light md:pt-1 md:text-right">© {new Date().getFullYear()} KARYNA<br className="hidden md:block" /> Tous droits réservés</p>
        </div>
      </div>
    </footer>
  );
}
